import { create } from 'zustand';

// Live comms buffer (docs/19 §3.2/§3.3). Threads + order lists are fetched by the
// Comms page; this store carries the live socket deltas (message:new, order:new,
// order:acked) so the open thread updates instantly and the sidebar unread badge
// reacts without a poll.

export type MessageDirection = 'FIELD_TO_HQ' | 'HQ_TO_FIELD';

export interface CommsMessage {
  id: string;
  platoonId: string;
  callsign: string;
  unitId?: string | null;
  direction: MessageDirection;
  content: string;
  priority: string;
  senderUserId?: string | null;
  senderDeviceId?: string | null;
  ackRequired: boolean;
  deliveredAt?: string | null;
  readAt?: string | null;
  ackedAt?: string | null;
  createdAt: string;
}

export interface CommsOrder {
  id: string;
  platoonId: string;
  callsign: string;
  unitId?: string | null;
  planId?: string | null;
  plan?: { id: string; name: string; status: string } | null;
  content: string;
  issuedById?: string | null;
  issuedAt: string;
  ackRequired: boolean;
  ackedAt?: string | null;
}

const CAP = 500;

interface CommsState {
  liveMessages: CommsMessage[];
  liveOrders: CommsOrder[];
  unread: number; // incoming FIELD_TO_HQ messages not yet seen on the Comms page

  pushMessage: (m: CommsMessage) => void;
  pushOrder: (o: CommsOrder) => void;
  applyOrderAck: (o: CommsOrder) => void;
  clearUnread: () => void;
}

export const useCommsStore = create<CommsState>((set) => ({
  liveMessages: [],
  liveOrders: [],
  unread: 0,

  pushMessage: (m) =>
    set((s) => {
      if (!m?.id) return s;
      const isIncoming = m.direction === 'FIELD_TO_HQ';
      return {
        liveMessages: [m, ...s.liveMessages.filter((x) => x.id !== m.id)].slice(0, CAP),
        unread: isIncoming ? s.unread + 1 : s.unread,
      };
    }),
  pushOrder: (o) =>
    set((s) => {
      if (!o?.id) return s;
      return { liveOrders: [o, ...s.liveOrders.filter((x) => x.id !== o.id)].slice(0, CAP) };
    }),
  applyOrderAck: (o) =>
    set((s) => {
      if (!o?.id) return s;
      const exists = s.liveOrders.some((x) => x.id === o.id);
      return {
        liveOrders: exists
          ? s.liveOrders.map((x) => (x.id === o.id ? { ...x, ...o } : x))
          : [o, ...s.liveOrders].slice(0, CAP),
      };
    }),
  clearUnread: () => set({ unread: 0 }),
}));
